// The video's length as a track, with a marker at every moment the AI
// reviewer pinned something to. Markers are buttons (keyboard and screen
// reader friendly); clicking the track itself jumps to that point.
import React from 'react';
import { TONE } from './qcFormat';
import { fmtTs } from './qcTimeline';

// Markers are 24px targets; neighbours can be a few seconds apart, and the
// note list beside the frame offers the same moments as full-width rows.
const MARK = 24;
const BADGE = 16;
const pct = (t, length) => (length > 0 ? Math.min(100, Math.max(0, (t / length) * 100)) : 0);

const describe = (moment) => `${moment.ts}: ${moment.events
    .map((e) => [e.kind === 'feedback' ? `Frame note (${e.label})` : e.label, e.note].filter(Boolean).join(', '))
    .join('; ')}`;

function Marker({ moment, length, selected, onSelect }) {
    const tone = TONE[moment.tone];
    const flag = moment.tone === 'flag';
    const label = describe(moment);
    return (
        <button type="button" className="vqc-marker" aria-label={label} title={label} aria-current={selected ? 'true' : undefined}
            onClick={() => onSelect(moment.t)}
            style={{ position: 'absolute', left: `${pct(moment.t, length)}%`, top: 0, marginLeft: -MARK / 2, width: MARK, height: MARK, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}>
            <span style={{
                width: flag ? 11 : 13, height: flag ? 11 : 13, background: tone.color, borderRadius: flag ? 2 : 13,
                transform: flag ? 'rotate(45deg)' : undefined,
                boxShadow: selected ? '0 0 0 2px #111936, 0 0 0 4px #fff' : '0 0 0 2px rgba(17,25,54,0.9)',
            }} />
            {moment.events.length > 1 && (
                <span aria-hidden="true" style={{ position: 'absolute', top: -7, right: -7, boxSizing: 'border-box', minWidth: BADGE, height: BADGE, padding: '0 4px', borderRadius: BADGE, background: '#fff', color: '#111936', fontSize: 11, fontWeight: 800, lineHeight: `${BADGE}px`, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                    {moment.events.length}
                </span>
            )}
        </button>
    );
}

export default function TimelineTrack({ length, moments, selectedTimes = new Set(), currentT, onSelect }) {
    const seek = (e) => {
        const box = e.currentTarget.getBoundingClientRect();
        if (!box.width || !length) return;
        onSelect(((e.clientX - box.left) / box.width) * length);
    };
    const at = pct(currentT, length);
    const ticks = length > 0 ? [0, 0.25, 0.5, 0.75, 1].map((f) => f * length) : [0];
    return (
        <div style={{ position: 'relative', padding: '0 6px' }}>
            <div style={{ position: 'relative', height: MARK + 6 }}>
                {moments.map((m) => <Marker key={m.t} moment={m} length={length} selected={selectedTimes.has(m.t)} onSelect={onSelect} />)}
            </div>
            {/* Click-to-seek is a mouse shortcut; markers and the frame strip cover the keyboard. */}
            <div className="vqc-track" onClick={seek} style={{ position: 'relative', height: 14, display: 'flex', alignItems: 'center' }}>
                <div style={{ position: 'absolute', left: 0, right: 0, height: 6, borderRadius: 6, background: 'rgba(255,255,255,0.14)' }} />
                <div style={{ position: 'absolute', left: 0, width: `${at}%`, height: 6, borderRadius: 6, background: 'linear-gradient(90deg, rgba(254,189,28,0.35), #febd1c)' }} />
                <div style={{ position: 'absolute', left: `${at}%`, marginLeft: -1, width: 2, height: 14, borderRadius: 2, background: '#febd1c', boxShadow: '0 0 8px rgba(254,189,28,0.8)' }} />
            </div>
            <div aria-hidden="true" style={{ position: 'relative', height: 18, marginTop: 4 }}>
                {ticks.map((t, i) => (
                    <span key={t} style={{
                        position: 'absolute', left: `${pct(t, length)}%`, fontSize: 12, color: 'rgba(255,255,255,0.72)', fontVariantNumeric: 'tabular-nums',
                        transform: i === 0 ? 'none' : i === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                    }}>{fmtTs(t)}</span>
                ))}
            </div>
        </div>
    );
}
